import {
  AttributionCookie,
  FacebookBrowserIdCookie,
  FacebookClickIdCookie,
  LandingPageUrlCookie,
  OTASignupProspectIdCookie,
  SpaazaReferralCodeCookie,
  UTMCampaignCookie,
  UTMMediumCookie,
  UTMSourceCookie
} from '@/enums/gtm'
import { MembershipTypes } from '@/enums/member'
import { defaultState, idPrefixes } from '@/enums/signup'
import { getCookieDomain } from '@/utils/cookie'
import { getImagePaths, serializeParameters } from '@/utils/general'
import { getMembershipTypeUrl, mapToWomenProspectProps, mapWomenAddressToProspectProps } from '@/utils/prospect'
import { getShieldSessionId } from '@/utils/shield'
import { defineStore } from 'pinia'
import { ref, computed, reactive, watch } from 'vue'
import { useRuntimeConfig, useCookie } from 'nuxt/app'
import { useAppStore } from '@/stores/app'
import type { LatestDesign } from '@/types/designs'

const SIGNUP_STORE_COOKIE = 'ota-v1-signup'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // 7 days

interface MutableFormState {
  [key: string]: any
  prospectId: string | null
  membershipType: string
  size: {
    reference: string | null
    label: string | null
  }
  design: {
    name: string | null
    slug: string | null
  }
  fixedDesign: {
    name: string | null
    slug: string | null
  }
  secondaryFixedDesign: {
    name: string | null
    slug: string | null
  }
  freeMembershipType: string
  freeSize: {
    reference: string | null
    label: string | null
  }
  freeDesign: {
    name: string | null
    slug: string | null
  }
  firstName: string
  lastName: string
  email: string
  password: string
  promotionCode: string
  promotionOffer: string
  consent: boolean
  optIn: boolean
  paymentId: string
  address: {
    [key: string]: any
    streetType: string
    street: string
    floor: string
    houseNumber: string | null
    annex: string
    houseNumberAddition: string
    building: string
    postalCode: string
    city: string
    region: string
    loqateAddressId: string | null
  }
}

interface MutableSignupState {
  form: MutableFormState
  addressConfirmed: boolean
  isSizeModalOpen: boolean
  currentStep: number
  previousRoute: string
  previousRouteParams: Record<string, any>
  nextRoute: string
  designs: Record<string, any>
  streetType: string
}

export type SignupPaymentErrorItem = {
  code: string
  message: string
  messageKey: string
}

export type CreateVerificationPaymentResponse = {
  redirectUrl?: string
  flaggedForFraud?: boolean
  errors?: SignupPaymentErrorItem[]
}

function serializeStoreState(state: MutableSignupState): string {
  try {
    return JSON.stringify(state)
  } catch (error) {
    console.error('Failed to serialize signup store state:', error)
    return ''
  }
}

function deserializeStoreState(serialized: any): Partial<MutableSignupState> | null {
  try {
    if (!serialized) return null

    if (typeof serialized === 'object') {
      return serialized as Partial<MutableSignupState>
    }

    if (typeof serialized === 'string') {
      if (serialized === '[object Object]') {
        console.warn('Invalid cookie value detected, clearing signup store cookie')
        return null
      }
      return JSON.parse(serialized)
    }

    return null
  } catch (error) {
    console.error('Failed to deserialize signup store state:', error)
    return null
  }
}

export const useSignupStore = defineStore(
  'signup',
  () => {
    // Initialize cookie for persistence
    const storeCookie = useCookie<string | null>(SIGNUP_STORE_COOKIE, {
      maxAge: COOKIE_MAX_AGE,
      path: '/',
      domain: getCookieDomain(),
      sameSite: 'lax'
    })

    // Try to restore state from cookie
    let savedState: Partial<MutableSignupState> | null = null
    if (storeCookie.value) {
      savedState = deserializeStoreState(storeCookie.value)
      // If deserialization failed or returned null, clear the invalid cookie
      if (savedState === null && storeCookie.value) {
        storeCookie.value = null
      }
    }

    // State
    const form = reactive<MutableFormState>({
      ...defaultState.form,
      ...(savedState?.form || {}),
      prospectId: savedState?.form?.prospectId ?? null,
      membershipType: savedState?.form?.membershipType ?? '',
      size: {
        reference: savedState?.form?.size?.reference ?? null,
        label: savedState?.form?.size?.label ?? null
      },
      design: {
        name: savedState?.form?.design?.name ?? null,
        slug: savedState?.form?.design?.slug ?? null
      },
      fixedDesign: {
        name: savedState?.form?.fixedDesign?.name ?? null,
        slug: savedState?.form?.fixedDesign?.slug ?? null
      },
      secondaryFixedDesign: {
        name: savedState?.form?.secondaryFixedDesign?.name ?? null,
        slug: savedState?.form?.secondaryFixedDesign?.slug ?? null
      },
      freeMembershipType: savedState?.form?.freeMembershipType ?? '',
      freeSize: {
        reference: savedState?.form?.freeSize?.reference ?? null,
        label: savedState?.form?.freeSize?.label ?? null
      },
      freeDesign: {
        name: savedState?.form?.freeDesign?.name ?? null,
        slug: savedState?.form?.freeDesign?.slug ?? null
      },
      firstName: savedState?.form?.firstName ?? '',
      lastName: savedState?.form?.lastName ?? '',
      email: savedState?.form?.email ?? '',
      password: savedState?.form?.password ?? '',
      promotionCode: savedState?.form?.promotionCode ?? '',
      promotionOffer: savedState?.form?.promotionOffer ?? '',
      consent: savedState?.form?.consent ?? false,
      optIn: savedState?.form?.optIn ?? false,
      paymentId: savedState?.form?.paymentId ?? '',
      address: {
        streetType: savedState?.form?.address?.streetType ?? '',
        street: savedState?.form?.address?.street ?? '',
        floor: savedState?.form?.address?.floor ?? '',
        houseNumber: savedState?.form?.address?.houseNumber ?? null,
        annex: savedState?.form?.address?.annex ?? '',
        houseNumberAddition: savedState?.form?.address?.houseNumberAddition ?? '',
        building: savedState?.form?.address?.building ?? '',
        postalCode: savedState?.form?.address?.postalCode ?? '',
        city: savedState?.form?.address?.city ?? '',
        region: savedState?.form?.address?.region ?? '',
        loqateAddressId: savedState?.form?.address?.loqateAddressId ?? null
      }
    })
    const addressConfirmed = ref(savedState?.addressConfirmed ?? false)
    const isSizeModalOpen = ref(savedState?.isSizeModalOpen ?? false)
    const currentStep = ref(savedState?.currentStep ?? 0)
    const previousRoute = ref(savedState?.previousRoute ?? '')
    const previousRouteParams = ref<Record<string, any>>(savedState?.previousRouteParams ?? {})
    const nextRoute = ref(savedState?.nextRoute ?? '')
    const designs = ref<Record<string, any>>(savedState?.designs ?? {})
    const streetType = ref(savedState?.streetType ?? '')

    function persistState() {
      try {
        const currentState: MutableSignupState = {
          form: { ...form },
          addressConfirmed: addressConfirmed.value,
          isSizeModalOpen: isSizeModalOpen.value,
          currentStep: currentStep.value,
          previousRoute: previousRoute.value,
          previousRouteParams: previousRouteParams.value,
          nextRoute: nextRoute.value,
          designs: designs.value,
          streetType: streetType.value
        }

        const serialized = serializeStoreState(currentState)
        if (serialized) {
          storeCookie.value = serialized
        }
      } catch (error) {
        console.error('Failed to persist signup store state:', error)
      }
    }

    watch(
      [form, addressConfirmed, isSizeModalOpen, currentStep, previousRoute, previousRouteParams, nextRoute, designs, streetType],
      () => {
        persistState()
      },
      { deep: true }
    )

    // Getters
    const activeBoxer = computed(() => {
      const slug = form.design.slug || 'boxer'
      const name = form.design.name || 'Boxer'
      const membershipType = form.freeMembershipType || form.membershipType || MembershipTypes.menOriginals
      const membershipTypeUrl = getMembershipTypeUrl(membershipType as any)

      return {
        slug,
        name,
        images: getImagePaths({
          s3: true,
          path: `/images/design/${slug}/products/${slug}-${membershipTypeUrl}-boxershort`,
          noMobile: true
        }),
        designPosterImages: getImagePaths({
          s3: true,
          path: `/images/design/${slug}/signup/${slug}-${membershipTypeUrl}-extra`,
          noMobile: true
        })
      }
    })

    // Actions
    function setAddressConfirmed(addressConfirmedValue: boolean) {
      addressConfirmed.value = addressConfirmedValue
    }

    function setIsSizeModalOpen({ isOpen }: { isOpen: boolean }) {
      isSizeModalOpen.value = isOpen
    }

    function setFormField({ key, value }: { key: string; value: any }) {
      form[key] = value
    }

    function setAddressFormField({ key, value }: { key: string; value: any }) {
      form.address[key] = value
    }

    function setDesigns({ designs: designsValue }: { designs: Record<string, any> }) {
      designs.value = designsValue
    }

    function setCurrentStep({ step }: { step: number }) {
      currentStep.value = step
    }

    function setPreviousRoute({ route }: { route: string }) {
      previousRoute.value = route
    }

    function setPreviousRouteParams({ params = {} }: { params?: Record<string, any> }) {
      previousRouteParams.value = params
    }

    function setNextRoute({ route }: { route: string }) {
      nextRoute.value = route
    }

    function resetSignupState(state: Partial<MutableSignupState> = {}) {
      setSignupState({ ...defaultState, ...state })
      if (Object.keys(state).length === 0) {
        storeCookie.value = null
      }
    }

    function setSignupState(variables: any) {
      const {
        form: formValue = {},
        addressConfirmed: addressConfirmedValue = false,
        isSizeModalOpen: isSizeModalOpenValue = false,
        designs: designsValue = {},
        currentStep: currentStepValue = 0,
        previousRoute: previousRouteValue = '',
        nextRoute: nextRouteValue = '',
        streetType: streetTypeValue = ''
      } = variables

      Object.assign(form, formValue)
      addressConfirmed.value = addressConfirmedValue
      isSizeModalOpen.value = isSizeModalOpenValue
      designs.value = designsValue
      currentStep.value = currentStepValue
      previousRoute.value = previousRouteValue
      streetType.value = streetTypeValue
      nextRoute.value = nextRouteValue
    }

    async function getAvailableDesigns({ productGroupSizes = '' }: { productGroupSizes?: string }) {
      const params = {
        free_signup: true,
        in_stock: true,
        product_group_sizes: productGroupSizes
      }

      const {
        public: { API_URL }
      } = useRuntimeConfig()
      return await $fetch(`${API_URL}/v2/designs?${serializeParameters(params)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function createProspect(variables: any) {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      const fbClickId = useCookie(FacebookClickIdCookie).value as any
      const browserId = useCookie(FacebookBrowserIdCookie).value as any
      const utmSource = useCookie(UTMSourceCookie).value as any
      const utmMedium = useCookie(UTMMediumCookie).value as any
      const utmCampaign = useCookie(UTMCampaignCookie).value as any
      const landingPageUrl = useCookie(LandingPageUrlCookie).value as any
      const otaAttribution = useCookie(AttributionCookie).value as any
      const referralCode = useCookie(SpaazaReferralCodeCookie).value as any

      const appStore = useAppStore()
      const { uuid } = await $fetch<{ uuid: string }>(`${apiUrl}/prospects`, {
        method: 'POST',
        body: {
          ...mapToWomenProspectProps({
            ...variables,
            country: variables.country || appStore.country,
            locale: appStore.locale,
            referralCode,
            fbClickId,
            browserId,
            utmSource,
            utmMedium,
            utmCampaign,
            landingPageUrl,
            otaAttribution
          })
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })

      const prospectCookie = useCookie(OTASignupProspectIdCookie, {
        expires: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
        path: '/',
        domain: getCookieDomain()
      })
      prospectCookie.value = uuid as any
      return uuid
    }

    async function updateProspectAccount(variables: any) {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      return await $fetch(`${apiUrl}/v2/prospects/${variables.prospectId}/account`, {
        method: 'PUT',
        body: variables,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function updateProspectAddress({ address, prospectId }: { address: any; prospectId: string }) {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      const appStore = useAppStore()
      const normalizedAddress = {
        ...address,
        houseNumber: address.houseNumber === '' ? null : address.houseNumber,
        country: address.country || appStore.country
      }
      return await $fetch(`${apiUrl}/prospects/${prospectId}/address`, {
        method: 'PUT',
        body: mapWomenAddressToProspectProps(normalizedAddress),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function updateMembershipType({
      membershipType,
      productGroupSizeDesigns,
      prospectId
    }: {
      membershipType: string
      productGroupSizeDesigns: any
      prospectId: string
    }) {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      return await $fetch(`${apiUrl}/prospects/${prospectId}/membership`, {
        method: 'PUT',
        body: {
          membershipType: `${idPrefixes.membershipType}${membershipType}`,
          productGroupSizeDesigns
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function createVerificationPayment({
      prospectId,
      paymentType = '',
      issuerId = null,
      paymentMethodNonce = null,
      successUrl = '',
      pendingUrl = '',
      failedUrl = '',
      declinedUrl = '',
      deviceData = '',
      externalIdentifier = null,
      billingMetadata = null
    }: any): Promise<CreateVerificationPaymentResponse> {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      const appStore = useAppStore()
      const shieldSessionId = await getShieldSessionId()
      return await $fetch<CreateVerificationPaymentResponse>(`${apiUrl}/prospects/${prospectId}/verification-payment`, {
        method: 'POST',
        body: {
          language: appStore.locale,
          paymentType,
          issuerId,
          paymentMethodNonce,
          deviceData,
          successUrl,
          pendingUrl,
          failedUrl,
          declinedUrl,
          shieldSessionId: shieldSessionId ?? null,
          externalIdentifier,
          billingMetadata
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function getAbandonedProspect({ uuid, _hash }: { uuid: string; _hash: string }) {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      return await $fetch(`${apiUrl}/v2/prospect/abandoned?uuid=${uuid}&_hash=${encodeURIComponent(_hash)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function useCouponCode({ prospectId, couponCode }: { prospectId: string; couponCode: string }) {
      const {
        public: { API_URL: apiUrl }
      } = useRuntimeConfig()
      return await $fetch(`${apiUrl}/v2/prospects/${prospectId}/coupon-code`, {
        method: 'PUT',
        body: {
          couponCode: `${couponCode}`.trim()
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function getLatestDesign({ women = false, men = true, addon = false, boys = true } = {}): Promise<LatestDesign[]> {
      const params = serializeParameters({ women, addon, men, boys })
        .replace(/=true&/g, ',')
        .replace(/=true/g, '')

      const {
        public: { API_URL }
      } = useRuntimeConfig()
      return await $fetch<LatestDesign[]>(`${API_URL}/v2/designs?latest=true&orientations=${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    async function addWaitingList({
      firstName,
      lastName,
      email,
      design,
      productGroupSize
    }: {
      firstName: string
      lastName: string
      email: string
      design: string
      productGroupSize: string
    }) {
      const {
        public: { API_URL }
      } = useRuntimeConfig()
      return await $fetch(`${API_URL}/v2/waiting-list`, {
        method: 'POST',
        body: {
          firstName,
          lastName,
          email,
          design,
          productGroupSize
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
    }

    return {
      // State
      form,
      addressConfirmed,
      isSizeModalOpen,
      currentStep,
      previousRoute,
      previousRouteParams,
      nextRoute,
      designs,
      streetType,
      // Getters
      activeBoxer,
      // Actions
      setAddressConfirmed,
      setIsSizeModalOpen,
      setFormField,
      setAddressFormField,
      setDesigns,
      setCurrentStep,
      setPreviousRoute,
      setPreviousRouteParams,
      setNextRoute,
      resetSignupState,
      setSignupState,
      getAvailableDesigns,
      createProspect,
      updateProspectAccount,
      updateProspectAddress,
      updateMembershipType,
      createVerificationPayment,
      getAbandonedProspect,
      useCouponCode,
      getLatestDesign,
      addWaitingList,
      // Cookie persistence
      persistState,
      clearPersistedState: () => {
        storeCookie.value = null
      }
    }
  }
)
